import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ArrowLeft, Save, Plus, Trash2, X, Upload, Users, Eye, ExternalLink,
  Image, FileText, MessageCircle, Edit
} from 'lucide-react';
import axios from 'axios';
import { AdminPageHeader, AdminPageShell, AdminPageBody } from '../../components/AdminPageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCustomPackages = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [showLeadDetail, setShowLeadDetail] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [content, setContent] = useState({
    hero_image: '',
    hero_title: 'Monte sua viagem do seu jeito.',
    hero_subtitle: 'Criamos roteiros personalizados nacionais e internacionais, adaptados ao seu perfil, orçamento e estilo de viagem.',
    services: [
      { icon: 'Plane', title: 'Passagens Aéreas', desc: 'Nacionais e internacionais com as melhores tarifas' },
      { icon: 'Hotel', title: 'Hospedagens', desc: 'Hotéis, pousadas e resorts selecionados' },
      { icon: 'MapPin', title: 'Passeios', desc: 'Experiências e atividades no destino' },
      { icon: 'Shield', title: 'Seguro Viagem', desc: 'Proteção completa para sua tranquilidade' },
      { icon: 'Ship', title: 'Cruzeiros', desc: 'As melhores rotas marítimas' },
      { icon: 'Car', title: 'Locação de Veículos', desc: 'Liberdade para explorar no seu ritmo' },
      { icon: 'Heart', title: 'Lua de Mel', desc: 'Roteiros românticos inesquecíveis' },
      { icon: 'Users', title: 'Grupos Privados', desc: 'Viagens exclusivas para seu grupo' },
    ],
    form_title: 'Solicite sua cotação',
    form_subtitle: 'Preencha abaixo e nossa equipe entra em contato em até 24h.',
    benefits: [
      { title: 'Atendimento Humanizado', desc: 'Consultores dedicados do início ao fim' },
      { title: 'Nacional e Internacional', desc: 'Destinos no Brasil e no mundo' },
      { title: 'Experiência em Grupos', desc: 'Anos organizando viagens em grupo' },
      { title: 'Pagamento Flexível', desc: 'Parcelamento facilitado' },
      { title: 'Suporte Real', desc: 'Acompanhamento antes, durante e depois' },
      { title: 'Roteiros Exclusivos', desc: 'Experiências personalizadas e únicas' },
    ],
    cta_title: 'Pronto para começar?',
    cta_subtitle: 'Fale com nossa equipe e transforme seu sonho de viagem em realidade.',
    whatsapp_number: '5521999999999'
  });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) { navigate('/admin/login'); return; }
    loadContent();
    loadLeads();
  }, [navigate]);

  const loadContent = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/packages/site-settings`);
      if (res.data?.custom_packages_content) {
        setContent(prev => ({ ...prev, ...res.data.custom_packages_content }));
      }
    } catch {}
    finally { setLoading(false); }
  };

  const loadLeads = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lead-forms/responses-by-form/custom-packages-page`);
      setLeads(res.data || []);
    } catch { setLeads([]); }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/packages/site-settings`, {
        custom_packages_content: content
      });
      setMessage({ type: 'success', text: 'Conteúdo salvo!' });
    } catch { setMessage({ type: 'error', text: 'Erro ao salvar' }); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file, field) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/themes/upload-campaign-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.url) {
        setContent(prev => ({ ...prev, [field]: res.data.url }));
      }
    } catch { alert('Erro ao enviar imagem'); }
  };

  const updateService = (idx, field, value) => {
    const s = [...content.services];
    s[idx] = { ...s[idx], [field]: value };
    setContent(prev => ({ ...prev, services: s }));
  };

  const updateBenefit = (idx, field, value) => {
    const b = [...content.benefits];
    b[idx] = { ...b[idx], [field]: value };
    setContent(prev => ({ ...prev, benefits: b }));
  };

  const fieldLabels = {
    name: 'Nome', whatsapp: 'WhatsApp', email: 'Email',
    destination: 'Destino', people: 'Pessoas', date: 'Data',
    trip_type: 'Tipo', budget: 'Orçamento', details: 'Detalhes'
  };

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Pacotes Personalizados"
        description="Página pública de captação para roteiros sob medida"
        Icon={FileText}
        iconClassName="text-blue-600"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => window.open('/pacotes-personalizados', '_blank')}>
              <Eye className="w-4 h-4 mr-1" /> Ver Página
            </Button>
            <Button onClick={saveContent} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        }
      />
      <AdminPageBody>
        {message.text && (
          <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ===== CONTEÚDO (2/3) ===== */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero */}
            <Card>
              <CardHeader><CardTitle className="text-base">Hero (Topo)</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Imagem de Fundo</Label>
                  <Input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], 'hero_image')} className="mt-1 text-sm" />
                  {content.hero_image && (
                    <div className="mt-2 relative">
                      <img src={content.hero_image} alt="" className="w-full h-32 object-cover rounded-lg" />
                      <button onClick={() => setContent(prev => ({...prev, hero_image: ''}))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">x</button>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Título</Label>
                  <Input value={content.hero_title} onChange={(e) => setContent(prev => ({...prev, hero_title: e.target.value}))} className="mt-1" />
                </div>
                <div>
                  <Label>Subtítulo</Label>
                  <textarea value={content.hero_subtitle} onChange={(e) => setContent(prev => ({...prev, hero_subtitle: e.target.value}))} rows={2} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" />
                </div>
                <div>
                  <Label>WhatsApp (número completo com DDI)</Label>
                  <Input value={content.whatsapp_number} onChange={(e) => setContent(prev => ({...prev, whatsapp_number: e.target.value}))} placeholder="5521999999999" className="mt-1" />
                </div>
              </CardContent>
            </Card>

            {/* Serviços */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Serviços ({content.services.length})</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setContent(prev => ({...prev, services: [...prev.services, {icon:'Star', title:'', desc:''}]}))}>
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {content.services.map((s, idx) => (
                  <div key={idx} className="flex gap-2 items-start border rounded-lg p-3 bg-gray-50">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input value={s.title} onChange={(e) => updateService(idx, 'title', e.target.value)} placeholder="Título" className="text-sm" />
                      <Input value={s.desc} onChange={(e) => updateService(idx, 'desc', e.target.value)} placeholder="Descrição" className="text-sm" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setContent(prev => ({...prev, services: prev.services.filter((_,i) => i !== idx)}))} className="text-red-400 h-8 w-8 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Benefícios */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Benefícios ({content.benefits.length})</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setContent(prev => ({...prev, benefits: [...prev.benefits, {title:'', desc:''}]}))}>
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {content.benefits.map((b, idx) => (
                  <div key={idx} className="flex gap-2 items-start border rounded-lg p-3 bg-gray-50">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <Input value={b.title} onChange={(e) => updateBenefit(idx, 'title', e.target.value)} placeholder="Título" className="text-sm" />
                      <Input value={b.desc} onChange={(e) => updateBenefit(idx, 'desc', e.target.value)} placeholder="Descrição" className="text-sm" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setContent(prev => ({...prev, benefits: prev.benefits.filter((_,i) => i !== idx)}))} className="text-red-400 h-8 w-8 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* CTA Final */}
            <Card>
              <CardHeader><CardTitle className="text-base">Seção Final</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Título CTA</Label>
                  <Input value={content.cta_title} onChange={(e) => setContent(prev => ({...prev, cta_title: e.target.value}))} className="mt-1" />
                </div>
                <div>
                  <Label>Subtítulo CTA</Label>
                  <Input value={content.cta_subtitle} onChange={(e) => setContent(prev => ({...prev, cta_subtitle: e.target.value}))} className="mt-1" />
                </div>
                <div>
                  <Label>Título do Formulário</Label>
                  <Input value={content.form_title} onChange={(e) => setContent(prev => ({...prev, form_title: e.target.value}))} className="mt-1" />
                </div>
                <div>
                  <Label>Subtítulo do Formulário</Label>
                  <Input value={content.form_subtitle} onChange={(e) => setContent(prev => ({...prev, form_subtitle: e.target.value}))} className="mt-1" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ===== LEADS (1/3) ===== */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" /> Leads Recebidos
                  </CardTitle>
                  <Badge className="bg-green-100 text-green-700">{leads.length}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <p className="text-center text-gray-400 py-6 text-sm">Nenhum lead ainda</p>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {leads.map((lead, idx) => {
                      const name = lead.answers?.find(a => a.question_id === 'name')?.answer || 'Sem nome';
                      const whatsapp = lead.answers?.find(a => a.question_id === 'whatsapp')?.answer || '';
                      const destination = lead.answers?.find(a => a.question_id === 'destination')?.answer || '';
                      return (
                        <div key={lead.id} className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setShowLeadDetail(lead)}>
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm text-gray-900">{name}</p>
                            <span className="text-xs text-gray-400">{new Date(lead.created_at).toLocaleDateString('pt-BR')}</span>
                          </div>
                          {destination && <p className="text-xs text-gray-500 mt-0.5">{destination}</p>}
                          {whatsapp && (
                            <a href={`https://wa.me/55${whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" 
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 mt-1 text-xs text-green-600 hover:underline">
                              <MessageCircle className="w-3 h-3" /> {whatsapp}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

      {/* Lead Detail Modal */}
      <Dialog open={!!showLeadDetail} onOpenChange={() => setShowLeadDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes do Lead</DialogTitle>
          </DialogHeader>
          {showLeadDetail && (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-gray-400">{new Date(showLeadDetail.created_at).toLocaleString('pt-BR')}</p>
              {showLeadDetail.answers?.map((a, idx) => (
                <div key={idx} className="flex gap-2 text-sm">
                  <span className="font-medium text-gray-600 min-w-[100px]">{fieldLabels[a.question_id] || a.question_id}:</span>
                  <span className="text-gray-900">{a.answer}</span>
                </div>
              ))}
              {(() => {
                const wa = showLeadDetail.answers?.find(a => a.question_id === 'whatsapp')?.answer;
                if (!wa) return null;
                return (
                  <a href={`https://wa.me/55${wa.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg text-sm font-medium mt-2">
                    <MessageCircle className="w-4 h-4" /> Falar no WhatsApp
                  </a>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </AdminPageBody>
    </AdminPageShell>
  );
};

export default AdminCustomPackages;
